import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { AdminSidebar } from './AdminDashboardPage';
import { uploadImage } from '../../services/uploadService';
import '../../css/adminherobanners.css';

const AdminHeroBannersPage = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image: '',
    ctaPrimaryLabel: '',
    ctaPrimaryLink: '',
    ctaSecondaryLabel: '',
    ctaSecondaryLink: '',
    order: 0,
    isActive: true
  });

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      const { data } = await api.get('/hero-banners');
      setBanners(data);
    } catch (err) {
      setError('Failed to load banners');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBanner) {
        await api.put(`/hero-banners/${editingBanner._id}`, formData);
      } else {
        await api.post('/hero-banners', formData);
      }
      await loadBanners();
      setShowModal(false);
      resetForm();
    } catch (err) {
      setError('Failed to save banner');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    try {
      await api.delete(`/hero-banners/${id}`);
      await loadBanners();
    } catch (err) {
      setError('Failed to delete banner');
      console.error(err);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await api.patch(`/hero-banners/${id}`, { isActive: !currentStatus });
      await loadBanners();
    } catch (err) {
      setError('Failed to update banner status');
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      image: '',
      ctaPrimaryLabel: '',
      ctaPrimaryLink: '',
      ctaSecondaryLabel: '',
      ctaSecondaryLink: '',
      order: 0,
      isActive: true
    });
    setEditingBanner(null);
    setSelectedFile(null);
  };

  const openModal = (banner = null) => {
    if (banner) {
      setFormData({
        title: banner.title,
        subtitle: banner.subtitle,
        image: banner.image,
        ctaPrimaryLabel: banner.ctaPrimaryLabel,
        ctaPrimaryLink: banner.ctaPrimaryLink,
        ctaSecondaryLabel: banner.ctaSecondaryLabel,
        ctaSecondaryLink: banner.ctaSecondaryLink,
        order: banner.order,
        isActive: banner.isActive
      });
      setEditingBanner(banner);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      const url = await uploadImage(selectedFile);
      setFormData((prev) => ({ ...prev, image: url }));
      setSelectedFile(null);
      document.getElementById('hero-banner-file-input').value = '';
    } catch (err) {
      console.error('Upload error:', err);
      alert(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="admin-layout"><AdminSidebar /><div className="admin-content">Loading...</div></div>;

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <div className="admin-header">
          <h1>Hero Banners</h1>
          <button className="btn-primary" onClick={() => openModal()}>Add New Banner</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="admin-table">
          <table>
            <thead>
              <tr>
                <th>Order</th>
                <th>Title</th>
                <th>Subtitle</th>
                <th>Image</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {banners.map((banner) => (
                <tr key={banner._id}>
                  <td>{banner.order}</td>
                  <td>{banner.title}</td>
                  <td>{banner.subtitle}</td>
                  <td>
                    <img src={banner.image} alt={banner.title} style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                  </td>
                  <td>
                    <button
                      className={`status-btn ${banner.isActive ? 'active' : 'inactive'}`}
                      onClick={() => toggleStatus(banner._id, banner.isActive)}
                    >
                      {banner.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>
                    <button className="btn-edit" onClick={() => openModal(banner)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(banner._id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <h2>{editingBanner ? 'Edit Banner' : 'Add New Banner'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Subtitle</label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Image</label>
                  <input
                    id="hero-banner-file-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ marginBottom: '0.5rem' }}
                  />
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleUpload}
                    disabled={uploading}
                    style={{ marginBottom: '0.5rem', display: 'block' }}
                  >
                    {uploading ? 'Uploading...' : 'Upload to Cloud'}
                  </button>
                  <input
                    type="url"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="Or enter image URL"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>First Button Name</label>
                  <input
                    type="text"
                    value={formData.ctaPrimaryLabel}
                    onChange={(e) => setFormData({ ...formData, ctaPrimaryLabel: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>First Button Link</label>
                  <input
                    type="url"
                    value={formData.ctaPrimaryLink}
                    onChange={(e) => setFormData({ ...formData, ctaPrimaryLink: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Second Button Name</label>
                  <input
                    type="text"
                    value={formData.ctaSecondaryLabel}
                    onChange={(e) => setFormData({ ...formData, ctaSecondaryLabel: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Secondnd Button Link</label>
                  <input
                    type="url"
                    value={formData.ctaSecondaryLink}
                    onChange={(e) => setFormData({ ...formData, ctaSecondaryLink: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>S.Order in List</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
                <div className="modal-actions">
                  <button type="submit" className="btn-primary">Save</button>
                  <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminHeroBannersPage;
